import { useEffect, useRef } from "react";
import { useAuthSession } from "../../../lib/auth/session";
import { useEffectiveOfflineMode } from "../../../lib/offline/status";
import { OFFLINE_CONFIG_RESOURCES, resolveResourceTtl } from "./catalog";
import { useOfflineCachePolicy } from "../state/OfflineCachePolicyProvider";
import { fetchResource, shouldFetchResource } from "./resourceUtils";
import { useOfflineWarmupDispatch } from "../state/OfflineWarmupStatusContext";

export const useOfflineConfigWarmup = () => {
  const { officerId } = useAuthSession();
  const { status, network } = useEffectiveOfflineMode();
  const policy = useOfflineCachePolicy();
  const inFlightRef = useRef(false);
  const warmupDispatch = useOfflineWarmupDispatch();

  useEffect(() => {
    if (!officerId) return;
    if (network !== "online") return;
    if (status !== "online") return;
    if (inFlightRef.current) return;

    inFlightRef.current = true;
    let cancelled = false;
    const warmupResources = OFFLINE_CONFIG_RESOURCES.filter((resource) => resource.warmup !== false);
    const total = warmupResources.length;
    if (total === 0) {
      warmupDispatch.start(0);
      inFlightRef.current = false;
      return undefined;
    }
    warmupDispatch.start(total);

    (async () => {
      for (let index = 0; index < warmupResources.length; index += 1) {
        const resource = warmupResources[index];
        if (cancelled) break;
        const ttlMs = resolveResourceTtl(resource, policy);
        warmupDispatch.progress(index, resource.key);
        const needsFetch = await shouldFetchResource(resource, ttlMs);
        if (cancelled) break;
        if (needsFetch) {
          await fetchResource(resource, ttlMs);
        }
        warmupDispatch.progress(index + 1, null);
      }
      if (!cancelled) {
        warmupDispatch.complete();
      }
    })().catch((error) => {
      console.warn("Offline config warmup failed", error);
      warmupDispatch.error(error instanceof Error ? error.message : undefined);
    });

    return () => {
      inFlightRef.current = false;
      cancelled = true;
    };
  }, [officerId, network, policy, status, warmupDispatch]);
};
